import { Capacitor } from "@capacitor/core";
import { LocalNotifications } from "@capacitor/local-notifications";
import { FileOpener } from "@capacitor-community/file-opener";
import { getToastInstance } from "@shared/helpers/toastHelpers";
import { useI18n } from "@/locales/useI18n";

const ANDROID_CHANNEL_ID = "vomi_downloads";
const ANDROID_IMPORTANCE_DEFAULT = 3;

let initialized = false;

interface DownloadedFileExtra {
  uri: string;
  mimeType: string;
}

export async function openDownloadedFile(uri: string, mimeType: string): Promise<void> {
  try {
    await FileOpener.open({ filePath: uri, contentType: mimeType });
  } catch (error) {
    // Ne gutati tiho — bez ovoga nema traga zašto otvaranje ne uspije (npr. loš URI/MIME).
    console.error("FileOpener.open nije uspio", { uri, mimeType }, error);
    getToastInstance().add({
      severity: "error",
      summary: useI18n().getText("common.documentOpenError"),
      life: 5000,
    });
  }
}

// Isti OS-level permission (Android POST_NOTIFICATIONS / iOS UNUserNotificationCenter) kao
// push — ako je korisnik već odobrio push, checkPermissions() ovdje već vraća "granted"
// bez novog prompta.
export async function initDownloadNotifications(): Promise<void> {
  if (!Capacitor.isNativePlatform() || initialized) return;
  initialized = true;

  if (Capacitor.getPlatform() === "android") {
    await LocalNotifications.createChannel({
      id: ANDROID_CHANNEL_ID,
      name: "Preuzimanja",
      importance: ANDROID_IMPORTANCE_DEFAULT,
    }).catch(() => undefined);
  }

  let permission = await LocalNotifications.checkPermissions();
  if (permission.display === "prompt" || permission.display === "prompt-with-rationale") {
    permission = await LocalNotifications.requestPermissions();
  }
  if (permission.display !== "granted") return;

  // Napomena: @capacitor/local-notifications na Androidu (LocalNotificationsPlugin.java) samo
  // override-a handleOnNewIntent — nema rukovanja cold-startom (app ubijen pa tap na notifikaciju
  // pokrene app ispočetka). Ako je app ugašen kad se notifikacija tapne, ovaj listener se neće
  // pozvati i fajl se neće otvoriti (poznato ograničenje pluginske biblioteke, ne rješava se iz JS-a).
  // Radi dok je app živ (foreground ili background, nije ubijen), što je uobičajen slučaj.
  await LocalNotifications.addListener("localNotificationActionPerformed", (action) => {
    const extra = action.notification.extra as DownloadedFileExtra | undefined;
    if (!extra?.uri) return;
    void openDownloadedFile(extra.uri, extra.mimeType);
  });
}

export async function notifyDocumentSaved(
  uri: string,
  fileName: string,
  mimeType = "application/pdf",
): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;

  await LocalNotifications.schedule({
    notifications: [
      {
        id: Date.now() % 2147483647,
        title: "Dokument spremljen",
        body: fileName,
        channelId: ANDROID_CHANNEL_ID,
        extra: { uri, mimeType } satisfies DownloadedFileExtra,
      },
    ],
  }).catch(() => undefined);
}
